import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { SoftseeHtmlRow } from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { SoftseeToolbar, SoftseeToolbarEnd } from './SoftseeToolbar'

/**
 * Wrapper-ul DataTables 1.10 folosit de filele Softsee de kituri. Formularele
 * din celule (Install / Update / Uninstall) trimit campurile vechi la POST-ul v4.
 */

export function SoftseeHtmlTable({
  id,
  tableClass,
  heads,
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  onSearch,
  onStart,
  onLength,
  onFormSubmit,
  filters,
}: {
  id: string
  tableClass: string
  heads: string[]
  rows: SoftseeHtmlRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  onFormSubmit?(form: HTMLFormElement): void
  /**
   * Filtrul filei (Kits | Custom Kits). Cand il are, sta la stanga randului
   * cautarii, iar cautarea la dreapta lui (`SoftseeToolbar`). Fara el - Update,
   * Uninstall - randul ramane cel de pana acum.
   */
  filters?: ReactNode
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)

  useEffect(() => {
    setDraft(search)
  }, [search])
  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  const searchBox = (
    <div id={`${id}_filter`} className="dataTables_filter">
      <label>
        Search:{' '}
        <input
          type="search"
          className="form-control input-sm"
          aria-controls={id}
          value={draft}
          onChange={(e) => {
            const value = e.target.value
            setDraft(value)
            if (typing.current !== null) window.clearTimeout(typing.current)
            typing.current = window.setTimeout(() => onSearch(value), 400)
          }}
        />
      </label>
    </div>
  )

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${id}_wrapper`}>
      {filters ? (
        <SoftseeToolbar>
          {filters}
          <SoftseeToolbarEnd>
            <div className="dataTableF">{searchBox}</div>
          </SoftseeToolbarEnd>
        </SoftseeToolbar>
      ) : (
        <div className="row">
          <div className="col-sm-6 dataTableF">{searchBox}</div>
        </div>
      )}
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${id}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table
            className={`table table-striped table-hover dataTable ${tableClass} no-footer`}
            width="100%"
            id={id}
            role="grid"
            onSubmit={(e) => {
              e.preventDefault()
              const form = e.target
              if (form instanceof HTMLFormElement) onFormSubmit?.(form)
            }}
          >
            <thead>
              <tr role="row">
                {heads.map((title, i) => (
                  <th
                    key={title}
                    className={`td-small a-left${i === 0 ? ' sorting_asc' : ' sorting'}`}
                    aria-sort={i === 0 ? 'ascending' : undefined}
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={heads.length} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={heads.length} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {row.cells.map((cell, c) => (
                      <td key={c} className="td-small a-left" dangerouslySetInnerHTML={{ __html: cell }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              Show{' '}
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
